/**
 * NotificationBell.js — the sidebar 🔔 notification bell, and the entry point
 * into the Notification Center dialog (Requests + Notifications).
 *
 * The center is a Modal with two tabs, not a Dropdown popover:
 *   • Requests      — RequestsPanel.js. Budget requests waiting on this user
 *                     (or raised by them), across every unit they touch —
 *                     previously visible only inside the specific unit's
 *                     Budget tab, one at a time.
 *   • Notifications  — budget-alert notifications (Approach B). Polls the
 *                     current user's alerts, shows an unread badge, and —
 *                     when the user has granted OS permission — raises a
 *                     native desktop notification for newly-arrived alerts.
 *
 * The popover version of this (a small fixed-width Dropdown panel) had no
 * room for the request-approve/decline actions and cramped the alert rows
 * enough to be awkward to read, so the whole thing moved into a proper
 * dialog. Creating/configuring alerts happens in the Budget panel's Alerts
 * dialog — this is only about consuming what's already been delivered.
 *
 * Each breach alert can be CLOSED here — handled, or dismissed with a reason.
 * "Read" only records that the row was seen, which cannot distinguish an alert
 * that was worked from one that was glanced at and forgotten; the daily digest
 * chases exactly the ones nobody closed, so closing has to be one click away
 * from where the alert is read.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Modal, Tabs, Tooltip, Button, Empty, Typography, Space, Tag, Input, message,
} from 'antd';
import { BellOutlined, BellFilled, ArrowUpOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  listBudgetAlertNotifications, markBudgetAlertNotificationsRead,
  handleBudgetAlertNotifications,
} from '../services/discoveryService';
import { listBudgetRequests } from '../services/orgStructureService';
import RequestsPanel from './RequestsPanel';

const { Text } = Typography;
const POLL_SECONDS = 45;
const POLL_MS = POLL_SECONDS * 1000;
const SEV = {
  critical: { icon: '🚨', color: '#d04a3b' },
  warning: { icon: '⚠️', color: '#d99100' },
  recovered: { icon: '✅', color: '#2f9e6b' },
  digest: { icon: '📋', color: '#6b7280' },
};
// Only a breach can be "closed" — a recovery or a digest is information, not a
// task, so offering Handled/Dismissed on them would just add noise to close.
const CLOSEABLE = new Set(['warning', 'critical']);
const OPEN_REQUEST = new Set(['pending_approval', 'pending_capacity']);

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

function timeAgo(iso) {
  if (!iso) {return '';}
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < SECONDS_PER_MINUTE) {return 'just now';}
  if (s < SECONDS_PER_HOUR) {return `${Math.floor(s / SECONDS_PER_MINUTE)}m ago`;}
  if (s < SECONDS_PER_DAY) {return `${Math.floor(s / SECONDS_PER_HOUR)}h ago`;}
  return `${Math.floor(s / SECONDS_PER_DAY)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [requestCount, setRequestCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [refreshing, setRefreshing] = useState(false);
  const seenRef = useRef(null);  // null until the first poll completes
  const requestsPanelRef = useRef(null);

  // Raise OS notifications for newly-arrived unread alerts. We skip the very
  // first poll so we don't replay history every time the app opens.
  const fireDesktop = useCallback((list) => {
    const ids = new Set(list.map((n) => n.id));
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      seenRef.current = ids;
      return;
    }
    const prev = seenRef.current;
    if (prev !== null) {
      list.forEach((n) => {
        if (!prev.has(n.id) && !n.read) {
          try { /* eslint-disable-next-line no-new */ new Notification(n.title, { body: n.body || '' }); }
          catch (_) { /* ignore */ }
        }
      });
    }
    seenRef.current = ids;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await listBudgetAlertNotifications({ limit: 20 });
      setItems(r.notifications || []);
      setUnread(r.unread || 0);
      fireDesktop(r.notifications || []);
    } catch (_) { /* keep last state on a transient failure */ }
  }, [fireDesktop]);

  // A lightweight companion poll for the badge only — RequestsPanel fetches
  // the full list (with names resolved) itself once the dialog is actually
  // open, so the bell doesn't pay for that just to sit in the sidebar.
  const refreshRequestCount = useCallback(async () => {
    try {
      const r = await listBudgetRequests({ scope: 'incoming' });
      // Only what waits on THIS viewer's decision — an admin can see every
      // open request in the organization, but decides almost none of them.
      setRequestCount((r.requests || []).filter(
        (x) => OPEN_REQUEST.has(x.status) && x.canDecide).length);
    } catch (_) { /* keep last count on a transient failure */ }
  }, []);

  useEffect(() => {
    refresh();
    refreshRequestCount();
    const t = setInterval(() => { refresh(); refreshRequestCount(); }, POLL_MS);
    return () => clearInterval(t);
  }, [refresh, refreshRequestCount]);

  // Re-fetches everything the dialog shows: the alert list/badge, the incoming
  // request count, and — via the ref — the Requests tab's own rows. The Modal
  // keeps RequestsPanel mounted across opens (destroyOnHidden is not set), so
  // its last load would otherwise go stale between one open and the next.
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refresh(), refreshRequestCount(), requestsPanelRef.current?.refresh()]);
    } finally {
      setRefreshing(false);
    }
  }, [refresh, refreshRequestCount]);

  const markAll = async () => {
    try { await markBudgetAlertNotificationsRead(); } catch (_) { /* ignore */ }
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    setUnread(0);
  };
  const markOne = async (id) => {
    try { await markBudgetAlertNotificationsRead([id]); } catch (_) { /* ignore */ }
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    setUnread((u) => Math.max(0, u - 1));
  };

  const applyClosed = (id, disposition) => {
    setItems((prev) => prev.map((i) => (
      i.id === id ? { ...i, read: true, disposition } : i
    )));
    setUnread((u) => Math.max(0, u - 1));
  };

  const closeAlert = async (n, disposition) => {
    if (disposition === 'handled') {
      try {
        await handleBudgetAlertNotifications([n.id], 'handled');
        applyClosed(n.id, 'handled');
      } catch (e) {
        message.error(e.message || 'Could not mark handled');
      }
      return;
    }
    // A dismissal without a reason is indistinguishable from an ignored alert,
    // so the API requires a note and the UI must collect one.
    let note = '';
    Modal.confirm({
      title: 'Dismiss this alert',
      content: (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Why is no action needed? This is recorded against the alert.
          </Text>
          <Input.TextArea
            rows={3} maxLength={300} style={{ marginTop: 8 }}
            placeholder="e.g. one-off migration run, already rebalanced"
            onChange={(e) => { note = e.target.value; }}
          />
        </div>
      ),
      okText: 'Dismiss',
      onOk: async () => {
        if (!note.trim()) {
          message.warning('A reason is required to dismiss');
          throw new Error('reason required');   // keeps the modal open
        }
        await handleBudgetAlertNotifications([n.id], 'dismissed', note.trim());
        applyClosed(n.id, 'dismissed');
      },
    });
  };

  const notificationsTab = (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button type="link" size="small" onClick={markAll} disabled={!unread} style={{ padding: 0, height: 'auto' }}>
          Mark all read
        </Button>
      </div>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No alerts" style={{ margin: '30px 0' }} />
      ) : (
        <div style={{ maxHeight: 440, overflowY: 'auto' }}>
          {items.map((n) => {
            const sev = SEV[n.severity] || SEV.warning;
            return (
              <div
                key={n.id}
                onClick={() => !n.read && markOne(n.id)}
                style={{
                  display: 'flex', gap: 10, padding: '11px 4px', borderBottom: '1px solid #f5f5f5',
                  background: n.read ? 'transparent' : 'rgba(201,169,97,.10)', cursor: n.read ? 'default' : 'pointer',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 7, flex: 'none', display: 'grid', placeItems: 'center',
                  background: `${sev.color}22`, fontSize: 14,
                }}>{sev.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{n.title}</div>
                  {n.body && (
                    <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'pre-line' }}>{n.body}</div>
                  )}
                  <div style={{ fontSize: 11, color: '#9aa0aa', marginTop: 3 }}>
                    {timeAgo(n.createdAt)}
                    {n.disposition ? (
                      <Tag
                        color={n.disposition === 'handled' ? 'green' : 'default'}
                        style={{ marginInlineStart: 6, marginInlineEnd: 0, fontSize: 10, lineHeight: '15px' }}
                      >
                        {n.disposition}
                      </Tag>
                    ) : null}
                  </div>
                  {CLOSEABLE.has(n.severity) && !n.disposition ? (
                    <Space size={4} style={{ marginTop: 5 }}>
                      <Button
                        size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11.5 }}
                        onClick={(e) => { e.stopPropagation(); closeAlert(n, 'handled'); }}
                      >
                        Mark handled
                      </Button>
                      <Text type="secondary" style={{ fontSize: 11 }}>·</Text>
                      <Button
                        size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11.5 }}
                        onClick={(e) => { e.stopPropagation(); closeAlert(n, 'dismissed'); }}
                      >
                        Dismiss…
                      </Button>
                    </Space>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const tabLabel = (icon, text, count) => (
    <Space size={4}>
      {icon} {text}
      {count > 0 ? <Badge count={count} size="small" /> : null}
    </Space>
  );

  const attentionCount = unread + requestCount;
  let tooltipTitle = 'Notifications';
  if (attentionCount) {
    const plural = attentionCount === 1 ? '' : 's';
    tooltipTitle = `${attentionCount} thing${plural} need your attention`;
  }

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        placement="right"
      >
        <Badge count={unread + requestCount} size="small" offset={[-2, 2]}>
          <span
            onClick={() => { setOpen(true); refreshAll(); }}
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
          >
            {(unread + requestCount) > 0
              ? <BellFilled style={{ fontSize: 18, color: 'var(--theme-primary)' }} />
              : <BellOutlined style={{ fontSize: 18, color: 'var(--theme-textSecondary, #8c8c8c)' }} />}
          </span>
        </Badge>
      </Tooltip>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        title={(
          <Space size={8}>
            Notification Center
            <Tooltip title="Refresh">
              <Button
                type="text" size="small" icon={<ReloadOutlined spin={refreshing} />}
                onClick={refreshAll}
              />
            </Tooltip>
          </Space>
        )}
        width={640}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'requests',
              label: tabLabel(<ArrowUpOutlined />, 'Requests', requestCount),
              children: <RequestsPanel ref={requestsPanelRef} onIncomingCountChange={setRequestCount} />,
            },
            {
              key: 'notifications',
              label: tabLabel(<BellOutlined />, 'Notifications', unread),
              children: notificationsTab,
            },
          ]}
        />
      </Modal>
    </>
  );
}
